import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Banknote, CheckCircle2, ChevronLeft, Scissors, Trash2 } from "lucide-react";
import { SaleHeaderProps } from "../interfaces";
import { AuthContext } from "../../../../App";
import { getTranslatedSaleStatus } from "../../../../translates/sales/sales";
import { SaleStatus } from "../../../../interfaces/sales";
import { profiles } from "../../../../profiles";
import ToolTip from "../../../../components/ToolTip/ToolTip";

export const SaleHeader: React.FC<SaleHeaderProps> = ({
  canRefund,
  code,
  hasCashPayment,
  openRefundModal,
  openPartialCancelModal,
  onConfirmCashPayment,
  onComplete,
  status,
  isTipAdjustment,
  bill,
}) => {
  const { user, canMarkExpiredCancelledSalesAsPaid } = useContext(AuthContext);
  const canMarkAsPaid =
    (status === 0 || status === 1 || status === 5 || status === 7) &&
    canMarkExpiredCancelledSalesAsPaid;
  const canConfirmCashPayment: boolean =
    status === SaleStatus.PorConfirmar && hasCashPayment;
  // A tip-adjustment sale is cancelled by cancelling its original sale (the API rejects cancelling it
  // directly), so neither full nor partial cancellation is offered here.
  const canPartialCancel = status === 2 && user?.id_profile === profiles.superAdmin && !isTipAdjustment;
  const canViewBillLink: boolean = user?.id_profile === profiles.superAdmin || user?.id_profile === profiles.venuesAdmin;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {`Venta ${code}`}
          </h1>
          <span
            className={`rounded-full px-3 py-1 text-center text-xs font-medium ${
              status === 0
                ? "bg-red-100 text-red-800"
                : status === 1
                  ? "bg-blue-100 text-blue-800"
                  : status === 2
                    ? "bg-green-100 text-green-800"
                    : status === 3
                      ? "bg-yellow-100 text-yellow-800"
                      : status === 4
                        ? "bg-red-100 text-red-800"
                        : status === 5
                          ? "bg-blue-100 text-blue-800"
                          : status === 6
                            ? "bg-gray-100 text-gray-800"
                            : status === 7
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
            }`}
          >
            {getTranslatedSaleStatus(status)}
          </span>
          {isTipAdjustment && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-center text-xs font-medium text-amber-800">
              Ajuste de propina
            </span>
          )}
        </div>
        {bill && canViewBillLink && (
          <Link
            to={`/panel/cuentas/${bill.uuid}`}
            className="flex w-fit items-center gap-1 text-sm font-medium transition-colors duration-300 hover:opacity-80 cursor-pointer"
            style={{ color: 'rgb(215 1 90)' }}
          >
            {`Cuenta ${bill.name}`}
          </Link>
        )}
        <Link
          to="/panel/ordenes"
          className="flex w-fit items-center gap-1 text-gray-500 transition-colors duration-300 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Volver a Órdenes</span>
        </Link>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {canConfirmCashPayment && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={onConfirmCashPayment}
          >
            <Banknote className="h-4 w-4 shrink-0 stroke-green-600" />
            <span className="text-sm font-medium text-green-600">
              Confirmar pago en efectivo
            </span>
          </button>
        )}
        {canMarkAsPaid && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={onComplete}
          >
            <CheckCircle2 className="h-4 w-4 shrink-0 stroke-blue-600" />
            <span className="text-sm font-medium text-blue-600">Marcar como pagada</span>
          </button>
        )}
        {canPartialCancel && (
          <ToolTip
            text={bill ? "Esta venta pertenece a una cuenta. La cancelación parcial se gestiona desde la cuenta." : undefined}
            placement="bottom"
          >
            <button
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={openPartialCancelModal}
              disabled={!!bill}
            >
              <Scissors className="h-4 w-4 shrink-0 stroke-orange-600" />
              <span className="text-sm font-medium text-orange-600">
                Cancelación parcial
              </span>
            </button>
          </ToolTip>
        )}
        {canRefund && !isTipAdjustment && (
          <button
            className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2.5 transition-colors duration-300 hover:bg-gray-100"
            onClick={openRefundModal}
          >
            <Trash2 className="h-4 w-4 shrink-0 stroke-red-600" />
            <span className="text-sm font-medium text-red-600">
              Cancelar venta
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
